import { FC } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Notification } from '../Notification';
import { BuyButtonProps, WithTotalProps } from '.';

export const commonStyle = css`
    font-family: Signika-Bold;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;

    color: ${({ theme }) => theme.buyButton.color.default};
`;

export const BuyButtonContainer = styled.button<
    WithTotalProps & { disabled: boolean }
>`
    padding: 0;
    border-radius: 1.125rem;
    min-height: 8.5rem;
    opacity: 1;

    ${({
        withTotal,
        disabled: buttonDisabled,
        theme: {
            buyButton: { enabled, disabled },
        },
    }) => {
        return css`
            cursor: ${buttonDisabled ? 'not-allowed' : 'pointer'};
            width: ${withTotal ? '26rem' : '14rem'};
            border: solid 0.113rem
                ${buttonDisabled ? disabled.border : enabled.border};
            background-image: linear-gradient(
                to bottom,
                ${buttonDisabled
                        ? disabled.backgroundGradient.upper
                        : enabled.backgroundGradient.upper}
                    1%,
                ${buttonDisabled
                        ? disabled.backgroundGradient.lower
                        : enabled.backgroundGradient.lower}
                    99%
            );
            box-shadow: ${withTotal
                        ? `0 0.375rem 0 0 ${enabled.boxShadow.first},`
                        : ''}
                    inset 0 0.313rem 0 0
                    ${buttonDisabled
                        ? disabled.boxShadow.second
                        : enabled.boxShadow.second},
                inset 0 -0.313rem 0 0 ${buttonDisabled ? disabled.boxShadow.third : enabled.boxShadow.third};
        `;
    }};
`;

export const FlexColWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    min-height: 7rem;
    width: 100%;
`;

export const FlexWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const ImageTextWrapper = styled.div<WithTotalProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;

    ${({ withTotal }) =>
        withTotal
            ? `
            height: 3.75rem;
        `
            : `
            height: auto;
        `};
`;

export const ImageWrapper = styled.div`
    position: relative;
`;

export const CartImage = styled.img<WithTotalProps>`
    ${({ withTotal }) => `
        width: ${withTotal ? '2.875rem' : '3.25rem'};
        height ${withTotal ? '3.438rem' : '4rem'};
    `};
`;

export const AutoBuyImage = styled.img`
    position: absolute;
    top: 1.5rem;
    right: -2.25rem;
    width: 3.875rem;
    height: 3.875rem;
`;

export const BuyButtonText = styled.div<
    WithTotalProps & { size: number; sizeWithTotal: number }
>`
    ${commonStyle};

    ${({ withTotal, size, sizeWithTotal }) => `
        font-size: ${withTotal ? `${sizeWithTotal}rem` : `${size}rem`};
        margin-top: ${withTotal ? '0.438rem' : '0.8rem'};
    `};
    margin-left: -0.938rem;
`;

export const TotalCostWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const NextGameText = styled.div`
    ${commonStyle};
    font-family: Signika-Medium;
    font-weight: normal;
    font-size: 1rem;
    margin-right: 0.5rem;
`;

export const TicketCost = styled.div`
    ${commonStyle};
    font-size: 1.5rem;
`;

export const BottomRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    padding: 0 1rem;
    gap: 0.5rem;
`;

export const BottomSection = styled.div`
    ${commonStyle};
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
    font-size: 1.5rem;
    line-height: 1.25;
`;

export const BottomSectionRight = styled(BottomSection)`
    justify-content: flex-end;
    text-align: right;
`;

export const TotalLabel = styled.span`
    flex-shrink: 0;
    color: ${({
        theme: {
            buyButton: { color },
        },
    }) => color.default};
`;

export const TotalValue = styled.span`
    overflow-wrap: break-word;
    word-break: break-word;
    min-width: 0;
    color: ${({
        theme: {
            buyButton: { color },
        },
    }) => color.totalValue};
`;

export const BuyButtonDesktop: FC<BuyButtonProps> = ({
    withTotal,
    total,
    consumedTokens,
    hideTokens,
    tickets,
    autoBuy,
    cardCost,
    isDisabled,
    handleClick,
    ...props
}) => {
    const { t } = useTranslation();

    const theme = useTheme();
    const { activationOn, activationOff } = theme.images;

    return (
        <BuyButtonContainer
            {...props}
            withTotal={withTotal}
            onClick={handleClick}
            disabled={isDisabled}
        >
            <FlexColWrapper>
                {!withTotal && (
                    <AutoBuyImage
                        src={autoBuy ? activationOn : activationOff}
                        alt="Auto Buy Image"
                    />
                )}
                <FlexWrapper>
                    <ImageTextWrapper withTotal={withTotal}>
                        <ImageWrapper>
                            {typeof tickets === 'number' && (
                                <Notification value={tickets} />
                            )}
                        </ImageWrapper>
                        <BuyButtonText
                            withTotal={withTotal}
                            size={t('buyBtn.buy.desktop.size')}
                            sizeWithTotal={t(
                                'buyBtn.buy.desktop.sizeWithTotal'
                            )}
                        >
                            {t('buyBtn.buy.msg')}
                        </BuyButtonText>
                    </ImageTextWrapper>
                </FlexWrapper>
                {withTotal ? (
                    <BottomRow>
                        {!hideTokens && (
                            <BottomSection>
                                <TotalLabel>{t('buyBtn.tokens')}:</TotalLabel>
                                <TotalValue>{consumedTokens}</TotalValue>
                            </BottomSection>
                        )}
                        <BottomSectionRight
                            style={
                                hideTokens
                                    ? {
                                          justifyContent: 'center',
                                          textAlign: 'center',
                                      }
                                    : undefined
                            }
                        >
                            <TotalLabel>{t('buyBtn.total')}:</TotalLabel>
                            <TotalValue>{total}</TotalValue>
                        </BottomSectionRight>
                    </BottomRow>
                ) : (
                    <TotalCostWrapper>
                        <NextGameText>{t('infoBox.nextGame')}</NextGameText>
                        <TicketCost>{cardCost}</TicketCost>
                    </TotalCostWrapper>
                )}
            </FlexColWrapper>
        </BuyButtonContainer>
    );
};
